
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { InteractionHistory } from './InteractionHistory';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "interaction-history"
    }}>{`Interaction history`}</h1>
    <p>{`You can share candidate interactions that occur in your software.`}</p>
    <p>{`This provides hirers with a consolidated view of interactions across your software and SEEK:`}</p>

    <InteractionHistory mdxType="InteractionHistory" />
    <p>{`Changes to candidate interaction history within your software should be reflected in the SEEK API within 60 seconds.`}</p>
    <h2 {...{
      "id": "data-structure"
    }}>{`Data structure`}</h2>
    <p>{`Individual interactions are represented by a `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidateProcessHistoryItem"
      }}><inlineCode parentName="a">{`CandidateProcessHistoryItem`}</inlineCode>{` type`}</a>{`.
Each item is associated with the `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode></a>{` of an uploaded candidate and listed under the `}<inlineCode parentName="p">{`seekProcessHistory`}</inlineCode>{` field.`}</p>
    <h3 {...{
      "id": "general-interactions"
    }}>{`General interactions`}</h3>
    <p>{`An interaction may occur outside of the context of a particular position:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsonc",
        "metastring": "inputType=\"CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput\"",
        "inputType": "\"CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput\""
      }}>{`{
  "action": {
    "code": "Note",
    "name": "Contact again later",
    "description": "Candidate not currently looking for work, try again in six months. Keen to stay in Melbourne."
  },
  "actionDate": "1999-07-26T23:48:58.775Z",
  "idempotencyId": "00000000-0000-0000-0000-000000000001",
  "primaryParties": [
    {
      "person": {
        // For interaction history, \`communication\` fields are not displayed in
        // SEEK Talent Search
        // They can be omitted or set to empty arrays
        "communication": {
          "email": [],
          "phone": []
        },
        "name": {
          "formattedName": "Terry A",
          "given": "Terry",
          "family": "Astley"
        }
      }
    }
  ]
  // \`positionProfile\` and \`seekSource\` are omitted for a general interaction
  // \`status\` is omitted for a non-\`CandidateWorkflowTransition\` action
}
`}</code></pre>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`action`}</inlineCode>{` has a freeform name, description and one of the following codes:`}</p>
        <table parentName="li">
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Code`}</strong></p></th>
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Definition`}</strong></p></th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`AgencySubmission`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A candidate was sent by an agency to the hirer for general consideration`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Document`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A document was attached`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Email`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`An email was sent`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Note`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A note was added`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`PhoneCall`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A phone call was made`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`PostPlacementActivity`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A post-placement activity occurred, e.g. a satisfaction survey`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Screening`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A screening activity occurred, e.g. an interview`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`VerificationActivity`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`A verification activity occurred, e.g. a police check`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`StatusChange`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`The availability of the candidate has changed`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Other`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Any other action that doesn’t fit into the other buckets`}</p></td>
            </tr>
          </tbody>
        </table>
        <p parentName="li"><inlineCode parentName="p">{`name`}</inlineCode>{` is the hirer-visible name for the workflow step.
For example, the hirer might have defined a “Second interview” step that maps to the SEEK API’s `}<inlineCode parentName="p">{`Screening`}</inlineCode>{` action code.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "placement-information"
    }}>{`Placement information`}</h3>
    <p>{`An interaction may be scoped to a given opportunity by providing the associated position profile:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsonc",
        "metastring": "inputType=\"CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput",
        "inputType": "\"CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput"
      }}>{`{
  "action": {
    "code": "CandidateWorkflowTransition",
    "name": "Initial offer",
    "description": "Progressed to offer stage"
  },
  "actionDate": "1999-12-26T12:31:43.185Z",
  "idempotencyId": "00000000-0000-0000-0000-000000000002",
  "primaryParties": [
    {
      "person": {
        "communication": {
          "email": [],
          "phone": []
        },
        "name": {
          "formattedName": "Hayden R",
          "given": "Hayden",
          "family": "Robinson"
        }
      }
    }
  ],
  // \`positionProfile\` and \`seekSource\` are required for placement information
  "positionProfile": {
    "profileId": "globalPublicTest:positionProfile:unposted:QnwpqZfzXu1NRmZw5tjswn"
  },
  "seekSource": {
    "name": "Recruitment Agency"
  },
  // \`status\` is required for a \`CandidateWorkflowTransition\` action
  "status": {
    "code": "Offer"
  }
}
`}</code></pre>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`action`}</inlineCode>{` may use any of the prior codes, with one addition:`}</p>
        <table parentName="li">
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Code`}</strong></p></th>
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Definition`}</strong></p></th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`CandidateWorkflowTransition`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`The status of the candidate for this opportunity has changed`}</p></td>
            </tr>
          </tbody>
        </table>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`seekSource`}</inlineCode>{` indicates the initial source of the candidate in the context of this opportunity.
The same value should be resent for each item in the process.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`status`}</inlineCode>{` must be provided for a `}<inlineCode parentName="p">{`CandidateWorkflowTransition`}</inlineCode>{` action,
following a rough order of progression:`}</p>
        <table parentName="li">
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Code`}</strong></p></th>
              <th parentName="tr" {...{
                "align": "left"
              }}><p parentName="th"><strong parentName="p">{`Definition`}</strong></p></th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Application`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Initial state in the hiring process`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`AgencyShortlist`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Shortlisted internally by an agency prior to submission to the hirer`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`AgencySubmission`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Sent by an agency to the hirer for consideration for this opportunity`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`HirerShortlist`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Shortlisted by the hirer`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Interview`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Interviewing/interviewed for the opportunity`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`TestingOrQualificationsCheck`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Completing/completed tests or verification of their qualifications`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`ReferenceCheck`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Having/had their references checked`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Offer`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Offered the position`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Successful`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`Accepted the position`}</p></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td"><inlineCode parentName="p">{`Unsuccessful`}</inlineCode></p></td>
              <td parentName="tr" {...{
                "align": "left"
              }}><p parentName="td">{`No longer being considered for the position`}</p></td>
            </tr>
          </tbody>
        </table>
      </li>
    </ul>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <h3 {...{
      "id": "create-a-candidate-process-history-item"
    }}>{`Create a candidate process history item`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/createCandidateProcessHistoryItem"
      }}><inlineCode parentName="a">{`createCandidateProcessHistoryItem`}</inlineCode>{` mutation`}</a>{` adds a record of an interaction between the candidate and a SEEK hirer.`}</p>
    <p>{`To avoid unintentional duplication of data, SEEK enforces uniqueness on a partner-provided `}<inlineCode parentName="p">{`idempotencyId`}</inlineCode>{`.
A request to create an existing item will receive a `}<inlineCode parentName="p">{`CreateCandidateProcessHistoryItemPayload_Conflict`}</inlineCode>{` result.
The existing item will not be updated; you can select its details from the conflict result.`}</p>
    <p>{`Keep track of the IDs assigned to the process history item to perform subsequent update and delete operations.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Mutation\\\"\",\"value\":\"mutation ($input: CreateCandidateProcessHistoryItemInput!) {\\n  createCandidateProcessHistoryItem(input: $input) {\\n    ... on CreateCandidateProcessHistoryItemPayload_Success {\\n      candidateProcessHistoryItem {\\n        id {\\n          value\\n        }\\n      }\\n    }\\n\\n    ... on CreateCandidateProcessHistoryItemPayload_Conflict {\\n      conflictingCandidateProcessHistoryItem {\\n        id {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":6918},\"end\":{\"line\":173,\"column\":4,\"offset\":7384},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"candidateProfile\\\": {\\n      \\\"profileId\\\": \\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n    },\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"action\\\": {\\n        \\\"code\\\": \\\"Email\\\",\\n        \\\"name\\\": \\\"Interview scheduling\\\",\\n        \\\"description\\\": \\\"Interview invitation sent\\\"\\n      },\\n      \\\"actionDate\\\": \\\"2020-09-28T23:48:58.775Z\\\",\\n      \\\"idempotencyId\\\": \\\"00000000-0000-0000-0000-000000000000\\\",\\n      \\\"primaryParties\\\": [{ \\\"organization\\\": { \\\"name\\\": \\\"Acme Corp\\\" } }]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":175,\"column\":1,\"offset\":7386},\"end\":{\"line\":193,\"column\":4,\"offset\":7923},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Success result\\\"\",\"value\":\"{\\n  \\\"createCandidateProcessHistoryItem\\\": {\\n    // Details of the newly created candidate process history item\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":195,\"column\":1,\"offset\":7925},\"end\":{\"line\":206,\"column\":4,\"offset\":8237},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Conflict result\\\"\",\"value\":\"{\\n  \\\"createCandidateProcessHistoryItem\\\": {\\n    // Details of the conflicting candidate process history item\\n    \\\"conflictingCandidateProcessHistoryItem\\\": {\\n      \\\"documentId\\\": {\\n        \\\"candidateProcessHistoryItem\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n          }\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":208,\"column\":1,\"offset\":8239},\"end\":{\"line\":223,\"column\":4,\"offset\":8654},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Mutation\\\"\",\"value\":\"mutation",
        "($input:": true,
        "CreateCandidateProcessHistoryItemInput!)": true,
        "{\\n": true,
        "": true,
        "createCandidateProcessHistoryItem(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "CreateCandidateProcessHistoryItemPayload_Success": true,
        "candidateProcessHistoryItem": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n\\n": true,
        "CreateCandidateProcessHistoryItemPayload_Conflict": true,
        "conflictingCandidateProcessHistoryItem": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":6918},\"end\":{\"line\":173,\"column\":4,\"offset\":7384},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"input\\\":": true,
        "\\\"candidateProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n": true,
        "},\\n": true,
        "\\\"candidateProcessHistoryItem\\\":": true,
        "\\\"action\\\":": true,
        "\\\"code\\\":": true,
        "\\\"Email\\\",\\n": true,
        "\\\"name\\\":": true,
        "\\\"Interview": true,
        "scheduling\\\",\\n": true,
        "\\\"description\\\":": true,
        "invitation": true,
        "sent\\\"\\n": true,
        "\\\"actionDate\\\":": true,
        "\\\"2020-09-28T23:48:58.775Z\\\",\\n": true,
        "\\\"idempotencyId\\\":": true,
        "\\\"00000000-0000-0000-0000-000000000000\\\",\\n": true,
        "\\\"primaryParties\\\":": true,
        "[{": true,
        "\\\"organization\\\":": true,
        "{": true,
        "\\\"Acme": true,
        "Corp\\\"": true,
        "}": true,
        "}]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":175,\"column\":1,\"offset\":7386},\"end\":{\"line\":193,\"column\":4,\"offset\":7923},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Success",
        "result\\\"\",\"value\":\"{\\n": true,
        "\\\"createCandidateProcessHistoryItem\\\":": true,
        "//": true,
        "Details": true,
        "of": true,
        "the": true,
        "newly": true,
        "created": true,
        "candidate": true,
        "process": true,
        "history": true,
        "item\\n": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":195,\"column\":1,\"offset\":7925},\"end\":{\"line\":206,\"column\":4,\"offset\":8237},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Conflict",
        "conflicting": true,
        "\\\"conflictingCandidateProcessHistoryItem\\\":": true,
        "\\\"documentId\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":208,\"column\":1,\"offset\":8239},\"end\":{\"line\":223,\"column\":4,\"offset\":8654},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: CreateCandidateProcessHistoryItemInput!) {
  createCandidateProcessHistoryItem(input: $input) {
    ... on CreateCandidateProcessHistoryItemPayload_Success {
      candidateProcessHistoryItem {
        id {
          value
        }
      }
    }

    ... on CreateCandidateProcessHistoryItemPayload_Conflict {
      conflictingCandidateProcessHistoryItem {
        id {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "update-a-candidate-process-history-item"
    }}>{`Update a candidate process history item`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/updateCandidateProcessHistoryItem"
      }}><inlineCode parentName="a">{`updateCandidateProcessHistoryItem`}</inlineCode>{` mutation`}</a>{` replaces a candidate process history item in its entirety:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: UpdateCandidateProcessHistoryItemInput!) {\\n  updateCandidateProcessHistoryItem(input: $input) {\\n    candidateProcessHistoryItem {\\n      id {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":229,\"column\":1,\"offset\":8812},\"end\":{\"line\":239,\"column\":4,\"offset\":9019},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"id\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\",\\n      \\\"action\\\": {\\n        \\\"code\\\": \\\"Email\\\",\\n        \\\"name\\\": \\\"Interview scheduling\\\",\\n        \\\"description\\\": \\\"Interview invitation sent to candidate\\\"\\n      },\\n      \\\"actionDate\\\": \\\"2020-09-28T23:48:58.775Z\\\",\\n      \\\"primaryParties\\\": [{ \\\"organization\\\": { \\\"name\\\": \\\"Not Acme Corp\\\" } }]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":241,\"column\":1,\"offset\":9021},\"end\":{\"line\":256,\"column\":4,\"offset\":9466},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"updateCandidateProcessHistoryItem\\\": {\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":258,\"column\":1,\"offset\":9468},\"end\":{\"line\":268,\"column\":4,\"offset\":9689},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "UpdateCandidateProcessHistoryItemInput!)": true,
        "{\\n": true,
        "": true,
        "updateCandidateProcessHistoryItem(input:": true,
        "$input)": true,
        "candidateProcessHistoryItem": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":229,\"column\":1,\"offset\":8812},\"end\":{\"line\":239,\"column\":4,\"offset\":9019},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"candidateProcessHistoryItem\\\":": true,
        "\\\"id\\\":": true,
        "\\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\",\\n": true,
        "\\\"action\\\":": true,
        "\\\"code\\\":": true,
        "\\\"Email\\\",\\n": true,
        "\\\"name\\\":": true,
        "\\\"Interview": true,
        "scheduling\\\",\\n": true,
        "\\\"description\\\":": true,
        "invitation": true,
        "sent": true,
        "to": true,
        "candidate\\\"\\n": true,
        "},\\n": true,
        "\\\"actionDate\\\":": true,
        "\\\"2020-09-28T23:48:58.775Z\\\",\\n": true,
        "\\\"primaryParties\\\":": true,
        "[{": true,
        "\\\"organization\\\":": true,
        "{": true,
        "\\\"Not": true,
        "Acme": true,
        "Corp\\\"": true,
        "}": true,
        "}]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":241,\"column\":1,\"offset\":9021},\"end\":{\"line\":256,\"column\":4,\"offset\":9466},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"updateCandidateProcessHistoryItem\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":258,\"column\":1,\"offset\":9468},\"end\":{\"line\":268,\"column\":4,\"offset\":9689},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UpdateCandidateProcessHistoryItemInput!) {
  updateCandidateProcessHistoryItem(input: $input) {
    candidateProcessHistoryItem {
      id {
        value
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "delete-a-candidate-process-history-item"
    }}>{`Delete a candidate process history item`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/deleteCandidateProcessHistoryItem"
      }}><inlineCode parentName="a">{`deleteCandidateProcessHistoryItem`}</inlineCode>{` mutation`}</a>{` removes a candidate process history item from the candidate profile.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: DeleteCandidateProcessHistoryItemInput!) {\\n  deleteCandidateProcessHistoryItem(input: $input) {\\n    candidateProcessHistoryItem {\\n      id {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":274,\"column\":1,\"offset\":9857},\"end\":{\"line\":284,\"column\":4,\"offset\":10064},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"id\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":286,\"column\":1,\"offset\":10066},\"end\":{\"line\":294,\"column\":4,\"offset\":10232},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"deleteCandidateProcessHistoryItem\\\": {\\n    \\\"candidateProcessHistoryItem\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":296,\"column\":1,\"offset\":10234},\"end\":{\"line\":306,\"column\":4,\"offset\":10455},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "DeleteCandidateProcessHistoryItemInput!)": true,
        "{\\n": true,
        "": true,
        "deleteCandidateProcessHistoryItem(input:": true,
        "$input)": true,
        "candidateProcessHistoryItem": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":274,\"column\":1,\"offset\":9857},\"end\":{\"line\":284,\"column\":4,\"offset\":10064},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"candidateProcessHistoryItem\\\":": true,
        "\\\"id\\\":": true,
        "\\\"globalPublicTest:candidateProcessHistoryItem:uploaded:KHAqBgxSfiMY1BjzJWannT\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":286,\"column\":1,\"offset\":10066},\"end\":{\"line\":294,\"column\":4,\"offset\":10232},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"deleteCandidateProcessHistoryItem\\\":": true,
        "\\\"value\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":296,\"column\":1,\"offset\":10234},\"end\":{\"line\":306,\"column\":4,\"offset\":10455},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: DeleteCandidateProcessHistoryItemInput!) {
  deleteCandidateProcessHistoryItem(input: $input) {
    candidateProcessHistoryItem {
      id {
        value
      }
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;